// Note: InvoiceTable copied from @css-xp/billing-v2 and modified -> invoice-tab-transaction-account.tsx (InvoiceTabTxA)
import React, { useCallback, useState, useRef, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import defaultTo from 'lodash/defaultTo';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import kebabCase from 'lodash/kebabCase';
import memoize from 'lodash/memoize';
import { differenceInDays, sub } from 'date-fns'; // Giving type error if used separately
import Button, { LoadingButton } from '@atlaskit/button';
import ExportIcon from '@atlaskit/icon/glyph/export';
import Lozenge from '@atlaskit/lozenge';
import get from 'lodash/get';
import { trackSearchEvent } from '@atlassian/temp-analytics';
import {
  ColGroup,
  CustomFilterCheckboxWrapper,
  CustomSearch,
  CustomizeView,
  DateFilterWithBadge,
  DateRangeType,
  FormattedAddress,
  FormattedDate,
  HeadCell,
  InvoiceUtil,
  SearchResultColumn,
  TabPanel,
  TableBody,
  TableHead,
  Tabs,
  useSidebar,
  SortableTableHead,
  InfoPanel,
  useFeatureToggle,
  ExpandableRow,
} from '@css-xp/base';
import {
  FetchCreditNoteWithInvoicesQuery,
  FetchCreditNoteWithInvoicesQueryVariables,
  FetchOverdueInvoicesForTransactionAccountQuery,
  FetchOverdueInvoicesForTransactionAccountQueryVariables,
  FetchInvoicesForTransactionAccountPpcQuery,
  InvoiceBillAndShipTo,
  InvoicePaymentMethod,
  InvoiceStatus,
  SortDirection,
  SortType,
  InvoicesFilterOptions,
  Invoice,
  Refunds,
  PaymentMethodType,
  PaymentMethod,
  PaymentOnTerms,
  CreditCardDetails,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  SoldToAddressDetailsLink,
  errorStateProp,
  emptyStateWithDescriptionProp,
  getSelectedColumns,
  FormatCurrency,
  NoData,
  TableHeading,
  BillingProfileNameFromId,
  SoldToAddress,
  LimitCurrencyDropdown,
} from '@css-xp/ppc-common';
import { useSortFromURLParams } from '@css-xp/ppc-custom-hooks';
import { CustomDynamicResultTable } from '@css-xp/base/custom-dynamic-result-table';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { transformFilterableFields } from '@atlassian/cssxp-utils-generic';
import {
  INVOICE_TABLE_SORT_KEYS,
  INVOICE_TABLE_TAB_FILTERS,
  routePaths,
  ENABLE_DESIGN_ITERATION_1,
  openAndPaidTabName,
  refundedTabName,
  refundedTabNameKey,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import {
  getPaymentMethodText,
  getRouteUrl,
  getSearchTextVariants,
  getFormattedDateFromTimeStamp,
  getSortDirectionMatchFromSortOptions,
  INVOICE_TABLE_UTILS,
  isPaypalPaymentMethod,
} from '@css-xp/ppc-utils';
import {
  emptyStatePropForInvoiceTable,
  emptyStatePropForWrongSearch,
  emptyStatePropForRefundsTable,
} from '@css-xp/ppc-common/src/state-props';
import { getTermDetailsInvoiceAmount, getTermDetailsInvoiceStatus } from '@css-xp/ppc-credit-details-page';
import { useFetchInvoicesForTransactionAccountPPCQuery } from '@css-xp/ppc-api';
import { PaypalPaymentMethodInTable } from '@css-xp/ppc-paypal';
import { InvoiceCell } from './invoice-cell';
import { fetchCreditNoteWithInvoices } from './queries/fetch-invoices-with-refunds';
import { fetchOverdueInvoicesForTransactionAccount } from './queries/fetch-overdue-invoices-for-transaction-account';
import { RefundChildRow } from './refund-child-row';
import './invoice-table.scss';

export const PAGE_TAB_SEARCH_RESULTS_LOAD_MORE = 15;

export const DefaultIsCreatedDescSorted = true;

type FilterOptions = { key: string; value: string }[] | Record<string, string>[];

interface Props {
  id: string;
  label?: string;
  transactionAccountId?: string;
  filters?: FilterOptions;
  // FIXME: Give appropriate type for searchOptions
  searchOptions?: any;
  shouldFetchOnlyOverdueInvoices?: boolean;
  shouldShowDeferredInvoicesColumns?: boolean;
  updateDownloadCsvOptions?: (value: {
    filterOptions?: InvoicesFilterOptions;
    selectedTab?: string;
    sortOptions?: SortType;
  }) => void;
  shouldHideCustomiseView?: boolean;
  columns?: string[];
  showDownloadCSVButton?: boolean;
  downloadInvoicesListCSV?: () => void;
  invoicesCSVListLoading?: boolean;
  isOrgSearchEnabled?: boolean;
  selectedInterfaceKey?: string; // One of openAndPaidTabNameKey | refundedTabNameKey
  isCreditNoteIconFunctional?: boolean;
  selectedCurrency?: string | null;
  showBillingProfileNumber?: boolean;
}

type ColumnList = HeadCell & SearchResultColumn;

interface FormData {
  searchTerm: string;
}

interface SorterColumnHeadProps {
  rowsInPage: number;
}

enum InvoiceTabStatus {
  OPEN = 'OPEN',
  PAID = 'PAID',
  UNCOLLECTIBLE = 'UNCOLLECTIBLE',
}

export enum InvoiceTableColumns {
  INVOICE_NUMBER = 'Invoice number',
  STATUS = 'Status',
  DUE = 'Due',
  ISSUED = 'Issued',
  AMOUNT = 'Amount',
  COMPANY = 'Company',
  CUSTOMER = 'Customer',
  SOLD_TO_ADDRESS = 'Sold-to address',
  PAYMENT_METHOD = 'Payment method',
  PO_NUMBER = 'PO number',
  DUNNING_DATE = 'Dunning date',
  OUTSTANDING_BALANCE = 'Outstanding balance',
  BILLING_PROFILE = 'Billing profile',
}

const dateRangeKeys = {
  createdDate: 'createdDate',
  dueDate: 'dueDate',
};

export const getIsFreeInvoice = memoize((paymentMethod?: InvoicePaymentMethod | null) => {
  return paymentMethod === InvoicePaymentMethod.NOPAYMENT;
});

export const getInvoiceStatus = (status: string | undefined | null) => {
  if (!status) return <NoData />;
  /**
   If the invoice status is "PAYMENT_DEFERRED" we consider it as "PAID", bcoz it will later be paid
   using bank-transfer, cheques, cash etc. Hence modifying the status to "PAID" below.
   */
  if (status === InvoiceStatus.PAYMENT_DEFERRED) {
    status = InvoiceStatus.PAID;
  }

  if (status === InvoiceStatus.PAID) {
    return (
      <span className="invoice-cell-lozenge">
        <Lozenge appearance="success" testId="success-status-lozenge">
          {status}
        </Lozenge>
      </span>
    );
  } else {
    return (
      <span className="invoice-cell-lozenge">
        <Lozenge appearance="inprogress" testId="inprogress-status-lozenge">
          {status}
        </Lozenge>
      </span>
    );
  }
};

// Due to https://hello.jira.atlassian.cloud/browse/DE-30389 PricingCurrencySupport is not used for Invoice Table
const getUpdatedFilterWithCurrency = (
  filterOptions: InvoicesFilterOptions,
  key: string,
  selectedCurrency?: string | null
) => {
  if (!selectedCurrency) {
    return filterOptions;
  }
  return {
    ...filterOptions,
    and: [...(filterOptions.and ? filterOptions.and : []), { key, value: selectedCurrency }],
  };
};

const getAppropriateDateRangeValue = (value: any, isCustomFilter?: boolean) => {
  const skipTimeFromDate = !!isCustomFilter;
  value.to = getFormattedDateFromTimeStamp(value.to, skipTimeFromDate);
  value.from = getFormattedDateFromTimeStamp(value.from, skipTimeFromDate);

  return value;
};

const getAppropriateDueDateRangeValue = (rangeValue: any, isCustomFilter?: boolean) => {
  const value = Object.assign({}, rangeValue);
  const skipTimeFromDate = !!isCustomFilter;
  value.from = getFormattedDateFromTimeStamp(rangeValue.to, skipTimeFromDate);
  value.to = getFormattedDateFromTimeStamp(rangeValue.from, skipTimeFromDate);

  return value;
};

const initialSelectedFieldsForStatus: string[] = [];
const initialStatusFilterFields = [
  {
    filterItem: 'Status',
    queryKey: 'status',
    iterableValues: transformFilterableFields<typeof InvoiceTabStatus>(InvoiceTabStatus),
    defaultSelectedFields: initialSelectedFieldsForStatus,
  },
];

const currentDate = new Date();
const pastDate = new Date('1970-01-01');
const yesterdayDate = sub(currentDate, { days: 1 });
const pastDueDateOptionValue = differenceInDays(currentDate, pastDate);
const dueDateRangeOption = [
  { text: 'Next 24 hours', value: -1 },
  { text: 'Next 7 days', value: -7 },
  { text: 'Next 14 days', value: -14 },
  { text: 'Next 30 days', value: -30 },
  { text: 'Next 90 days', value: -90 },
  { text: 'Next 12 months', value: -365 },
  {
    text: 'In the past',
    value: pastDueDateOptionValue,
    from: yesterdayDate,
    to: pastDate,
  },
  { text: 'Custom', value: 0 },
];

const getCompanySortType = (isCompanyAscSorted?: boolean): SortType => {
  return {
    sortFields: [INVOICE_TABLE_SORT_KEYS.COMPANY_NAME, INVOICE_TABLE_SORT_KEYS.ID],
    sortFieldsDirections: [isCompanyAscSorted ? SortDirection.ASC : SortDirection.DESC, SortDirection.DESC],
  };
};

const getCreatedSortType = (isCreatedDescSorted?: boolean): SortType => {
  return {
    sortFields: [INVOICE_TABLE_SORT_KEYS.CREATED_DATE, INVOICE_TABLE_SORT_KEYS.ID],
    sortFieldsDirections: [isCreatedDescSorted ? SortDirection.DESC : SortDirection.ASC, SortDirection.DESC],
  };
};

const getDueDateSortType = (isDueDateDescSorted?: boolean): SortType => {
  return {
    sortFields: [INVOICE_TABLE_SORT_KEYS.DUE_DATE, INVOICE_TABLE_SORT_KEYS.ID],
    sortFieldsDirections: [isDueDateDescSorted ? SortDirection.DESC : SortDirection.ASC, SortDirection.DESC],
  };
};

const getAmountSortType = (isAmountAscSorted?: boolean): SortType => {
  return {
    sortFields: [INVOICE_TABLE_SORT_KEYS.AMOUNT, INVOICE_TABLE_SORT_KEYS.ID],
    sortFieldsDirections: [isAmountAscSorted ? SortDirection.ASC : SortDirection.DESC, SortDirection.DESC],
  };
};

export const invoiceTableCommonHeadersMap = (): any => {
  return {
    [InvoiceTableColumns.INVOICE_NUMBER]: {
      name: InvoiceTableColumns.INVOICE_NUMBER,
      content: InvoiceTableColumns.INVOICE_NUMBER,
      customClassNames: 'col-invoice-number',
    },
    [InvoiceTableColumns.STATUS]: {
      name: InvoiceTableColumns.STATUS,
      content: InvoiceTableColumns.STATUS,
      customClassNames: 'col-status',
    },
    [InvoiceTableColumns.DUE]: {
      name: InvoiceTableColumns.DUE,
      content: InvoiceTableColumns.DUE,
      isMandatory: false,
      isChecked: true,
      customClassNames: 'col-due',
    },
    [InvoiceTableColumns.ISSUED]: {
      name: InvoiceTableColumns.ISSUED,
      content: InvoiceTableColumns.ISSUED,
      isMandatory: false,
      isChecked: true,
      customClassNames: 'col-issued',
    },
    [InvoiceTableColumns.AMOUNT]: {
      name: InvoiceTableColumns.AMOUNT,
      content: InvoiceTableColumns.AMOUNT,
      isMandatory: false,
      isChecked: true,
      customClassNames: 'col-amount',
    },
    [InvoiceTableColumns.COMPANY]: {
      name: InvoiceTableColumns.COMPANY,
      content: InvoiceTableColumns.COMPANY,
      customClassNames: 'col-company',
    },
    [InvoiceTableColumns.CUSTOMER]: {
      name: InvoiceTableColumns.CUSTOMER,
      content: InvoiceTableColumns.CUSTOMER,
      customClassNames: 'col-customer',
    },
    [InvoiceTableColumns.SOLD_TO_ADDRESS]: {
      name: InvoiceTableColumns.SOLD_TO_ADDRESS,
      content: InvoiceTableColumns.SOLD_TO_ADDRESS,
      customClassNames: 'col-sold-to-address',
    },
    [InvoiceTableColumns.PAYMENT_METHOD]: {
      name: InvoiceTableColumns.PAYMENT_METHOD,
      content: InvoiceTableColumns.PAYMENT_METHOD,
      customClassNames: 'col-15 col-payment-method',
    },
    [InvoiceTableColumns.PO_NUMBER]: {
      name: InvoiceTableColumns.PO_NUMBER,
      content: InvoiceTableColumns.PO_NUMBER,
      isMandatory: false,
      isChecked: true,
      customClassNames: 'col-po-number',
    },
    [InvoiceTableColumns.BILLING_PROFILE]: {
      name: InvoiceTableColumns.BILLING_PROFILE,
      content: InvoiceTableColumns.BILLING_PROFILE,
      customClassNames: 'col-billing-profile',
    },
  };
};

const defaultColumns = [
  InvoiceTableColumns.INVOICE_NUMBER,
  InvoiceTableColumns.STATUS,
  InvoiceTableColumns.AMOUNT,
  InvoiceTableColumns.ISSUED,
  InvoiceTableColumns.DUE,
  InvoiceTableColumns.BILLING_PROFILE,
  InvoiceTableColumns.PO_NUMBER,
];

const InvoiceTable = React.memo(
  ({
    id,
    label,
    transactionAccountId,
    searchOptions,
    filters = [],
    shouldFetchOnlyOverdueInvoices = false,
    shouldShowDeferredInvoicesColumns = false,
    updateDownloadCsvOptions,
    shouldHideCustomiseView,
    showDownloadCSVButton,
    downloadInvoicesListCSV,
    invoicesCSVListLoading,
    columns = defaultColumns,
    isOrgSearchEnabled,
    selectedInterfaceKey,
    isCreditNoteIconFunctional,
    selectedCurrency,
    showBillingProfileNumber,
  }: Props) => {
    const isInitialMount = useRef(true);
    const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
    const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
    const [statusFilterOptions, setStatusFilterOptions] = useState<Record<string, string>[]>(INVOICE_TABLE_TAB_FILTERS);
    const [searchInputOptions, setSearchInputOptions] = useState<{ key: string; value: string }[]>([]);
    const [selectedCreatedDateRangeValue, setSelectedCreatedDateRangeValue] = useState('');
    const [selectedDueDateRangeValue, setSelectedDueDateRangeValue] = useState('');
    const [createdDateRange, setCreatedDateRange] = useState<{ key: string; value: string }[]>([]);
    const [dueDateRange, setDueDateRange] = useState<{ key: string; value: string }[]>([]);
    // Using `setStatusFilterResetCount` to reset the internal state for status filter component `CustomFilterCheckboxWrapper`
    const [statusFilterResetCount, setStatusFilterResetCount] = useState<number>(0);
    // Using statusFilterFields state to update defaultSelectedFields for Status filter which is changed
    // later based on the URL params ?status=<VALUE>, if present.
    const [statusFilterFields, setStatusFilterFields] = useState(initialStatusFilterFields);
    const [isSearched, setIsSearched] = useState(false);
    const [isStatusFilterApplied, setIsStatusFilterApplied] = useState(false);
    const { sortKey, sortOrder, filterOptionsFromUrl } = useSortFromURLParams();
    const defaultSortOptions = {
      sortFields: [INVOICE_TABLE_SORT_KEYS.ID],
      sortFieldsDirections: [SortDirection.DESC],
    };
    if (sortKey && sortOrder) {
      defaultSortOptions.sortFields.unshift(sortKey);
      defaultSortOptions.sortFieldsDirections.unshift(sortOrder as SortDirection);
    } else {
      defaultSortOptions.sortFields.unshift(INVOICE_TABLE_SORT_KEYS.CREATED_DATE);
      defaultSortOptions.sortFieldsDirections.unshift(SortDirection.DESC);
    }

    const [sortOptions, setSortOptions] = useState<SortType>(defaultSortOptions);
    const [currency, setCurrency] = useState<string>('');

    const hasPayOnAccountFromUrl = filterOptionsFromUrl?.find(
      (filter: { key: string }) => filter.key === 'hasPayOnAccount'
    )?.value;

    const { isSidebarOpen } = useSidebar();

    const isCompanyAscSortedRef = useRef<boolean | undefined>(
      getSortDirectionMatchFromSortOptions(defaultSortOptions, INVOICE_TABLE_SORT_KEYS.COMPANY_NAME, SortDirection.ASC)
    );
    const isAmountAscSortedRef = useRef<boolean | undefined>(
      getSortDirectionMatchFromSortOptions(defaultSortOptions, INVOICE_TABLE_SORT_KEYS.AMOUNT, SortDirection.ASC)
    );
    const isDueDateDescSortedRef = useRef<boolean | undefined>(
      getSortDirectionMatchFromSortOptions(defaultSortOptions, INVOICE_TABLE_SORT_KEYS.DUE_DATE, SortDirection.DESC)
    );
    const isCreatedDescSortedRef = useRef<boolean | undefined>(
      getSortDirectionMatchFromSortOptions(defaultSortOptions, INVOICE_TABLE_SORT_KEYS.CREATED_DATE, SortDirection.DESC)
    );

    const [isEnabled] = useFeatureToggle();

    const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);

    const appropriateTabFilters = INVOICE_TABLE_TAB_FILTERS;

    const isCurrencyFilterApplied = useMemo(() => {
      return isMultiCurrencyChangesEnabled && currency !== '';
    }, [currency, isMultiCurrencyChangesEnabled]);

    /** rowsPerPage param is required to set the updated search options */
    const commonQuerySearchOptions = INVOICE_TABLE_UTILS.getCommonQuerySearchOptions(
      sortOptions,
      searchOptions,
      rowsPerPage
    );

    const [filterOptions, setFilterOptions] = useState<InvoicesFilterOptions>(
      INVOICE_TABLE_UTILS.getFilterOptions({ shouldShowDeferredInvoicesColumns, filters: filters })
    );

    const [totalCount, setTotalCount] = useState<number>(0);
    const [hasNoResults, setHasNoResults] = useState<boolean>(false);

    const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
    const isPaypalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_PAYPAL_SUPPORT);
    const isRefundedInterface = selectedInterfaceKey === refundedTabNameKey;

    React.useEffect(() => {
      // Update the status field and due date filters based on the search params in the URL, if present
      // E.g: http://localhost:3000/account/0e1bbef5-ded1-491c-b992-3df4ebfc4cc3/invoices?status=OPEN&dueDate=PAST

      // checking if the URL contains any status filter
      const statusfilter = filterOptionsFromUrl?.find((filter: { key: string }) => filter.key === 'status');
      // checking if the URL contains dueDate filter
      const dueDateFilter = filterOptionsFromUrl?.find((filter: { key: string }) => filter.key === 'dueDate');

      const status = statusfilter ? statusfilter.value : '';
      const dueDate = dueDateFilter ? dueDateFilter.value : '';

      if (status === InvoiceTabStatus.OPEN) {
        // Add defaultSelectedFields
        const newStatusFilterFields = [
          {
            ...statusFilterFields[0],
            defaultSelectedFields: [InvoiceTabStatus.OPEN],
          },
        ];

        setStatusFilterFields(newStatusFilterFields);
      }

      if (dueDate === 'PAST') {
        const dueDateRangeValue = pastDueDateOptionValue + '';
        setSelectedDueDateRangeValue(dueDateRangeValue);
        const selectedOption = dueDateRangeOption.find((option) => option.value.toString() === dueDateRangeValue);

        if (selectedOption && selectedOption.from && selectedOption.to) {
          handleDateRangeFilter(dateRangeKeys.dueDate, {
            // Keys are interchanged in handleDateRangeFilter i.e., `IsGte` (from) should
            // be for pastDate which is the `selectedOption.to` value and `IsLte` (to) should be for yesterday which
            // is the `selectedOption.from` value. Hence, sending interchanged values.
            from: new Date(selectedOption.to).toISOString(),
            to: new Date(selectedOption.from).toISOString(),
          });
        }
      }
    }, []); // Adding statusFilterFields or removing empty dependency array will cause infinite loop

    React.useEffect(() => {
      /* removed refetch logic from useEffect because every time any of the dependencies (`filterOptions`,`statusFilterOptions`) changes,
      the `useEffect` would trigger, causing multiple calls to refetch the data.
      useFetchInvoicesForTransactionAccountPPCQuery is already taking care of refetching the data when the filterOptions changes.
      refetch is still needed for pageChange and rowsChange, hence moved the logic to the handler functions seperately. * */

      updateDownloadCsvOptions?.({ filterOptions, selectedTab: selectedTabKey, sortOptions });
    }, [filterOptions, sortOptions, rowsPerPage, currentPage, statusFilterOptions]);

    React.useEffect(() => {
      // Since, we are trying to replicate the tab behaviour using button clicks, instead of passing the onClick event,
      // we are passing the selectedTabKey as a dependency and using useEffect to handle the tab selection. This is to be
      // run only after the initial mount.
      if (isInitialMount.current) {
        isInitialMount.current = false;
      } else {
        if (isDesignIteration1Enabled) {
          isRefundedInterface ? handleTabSelect(refundedTabName) : handleTabSelect(openAndPaidTabName);
        }
      }
    }, [selectedInterfaceKey]);

    // Fetch appropriate invoices based on `skip` condition
    const overdueInvoicesData = useQuery<
      FetchOverdueInvoicesForTransactionAccountQuery,
      FetchOverdueInvoicesForTransactionAccountQueryVariables
    >(fetchOverdueInvoicesForTransactionAccount, {
      variables: {
        id: id,
        searchOptions: commonQuerySearchOptions,
      },
      displayName: 'fetchOverdueInvoicesForTransactionAccount',
      notifyOnNetworkStatusChange: true,
      skip: !shouldFetchOnlyOverdueInvoices,
    });

    const allInvoicesData = useFetchInvoicesForTransactionAccountPPCQuery({
      txaId: id,
      searchOptions: commonQuerySearchOptions,
      filterOptions: getUpdatedFilterWithCurrency(filterOptions, 'pricingCurrency', selectedCurrency),
      skipOption: shouldFetchOnlyOverdueInvoices,
    });

    const deferredPaymentMethod = allInvoicesData?.data?.transactionAccount?.availablePaymentMethods?.nodes?.filter(
      (paymentMethod: PaymentMethod | null) => paymentMethod?.type === PaymentMethodType?.DEFERRED
    ) as PaymentOnTerms[];

    const hasPayByStatement =
      deferredPaymentMethod && deferredPaymentMethod?.length > 0
        ? get(deferredPaymentMethod[0], 'terms.payOnStatement')
        : false;

    const { data: refundsData, error: refundError, loading: refundLoading, refetch: refetchRefund } = useQuery<
      FetchCreditNoteWithInvoicesQuery,
      FetchCreditNoteWithInvoicesQueryVariables
    >(fetchCreditNoteWithInvoices, {
      variables: {
        id: id,
        searchOptions: commonQuerySearchOptions,
        filterOptions: getUpdatedFilterWithCurrency(filterOptions, 'currency', selectedCurrency),
      },
      displayName: 'fetchCreditNoteWithInvoices',
      notifyOnNetworkStatusChange: true,
    });

    const [totalRefundInvoices, setTotalRefundInvoices] = useState<number>(0);
    React.useEffect(() => {
      setTotalRefundInvoices(refundsData?.transactionAccount?.refundedInvoices?.totalCount ?? 0);
    }, [refundsData, totalRefundInvoices]);
    // Fetch pending invoices
    // const { data: pendingInvoiceData } = useQuery<
    //   generatedTypesNew.FetchPendingInvoicesForTransactionAccountQuery,
    //   generatedTypesNew.FetchPendingInvoicesForTransactionAccountQueryVariables
    // >(fetchPendingInvoicesForTransactionAccount, {
    //   variables: {
    //     id: id,
    //   },
    //   displayName: 'fetchPendingInvoicesForTransactionAccount',
    // });

    const appropriateInvoicesData = shouldFetchOnlyOverdueInvoices ? overdueInvoicesData : allInvoicesData;
    const { loading, error, data, refetch } = appropriateInvoicesData;

    React.useEffect(() => {
      if (shouldFetchOnlyOverdueInvoices) {
        const transactionAccount: FetchOverdueInvoicesForTransactionAccountQuery['transactionAccount'] | undefined =
          data?.transactionAccount;
        setTotalCount(transactionAccount?.overdueInvoices?.totalCount ?? 0);
      } else {
        const transactionAccount: FetchInvoicesForTransactionAccountPpcQuery['transactionAccount'] | undefined =
          data?.transactionAccount;
        setTotalCount(transactionAccount?.invoices?.totalCount ?? 0);
      }

      setHasNoResults(totalCount === 0);
    }, [data, totalCount, shouldFetchOnlyOverdueInvoices]);

    const CompanyHead = ({ columnName }: { columnName: string }) => {
      const handleSorting = () => {
        isCompanyAscSortedRef.current = !isCompanyAscSortedRef.current;
        isAmountAscSortedRef.current = undefined;
        isCreatedDescSortedRef.current = undefined;
        isDueDateDescSortedRef.current = undefined;
        const newSortOptions: SortType = getCompanySortType(isCompanyAscSortedRef.current);
        setSortOptions(newSortOptions);
        setCurrentPage(DEFAULT_PAGE);
      };
      return (
        <SortableTableHead
          isAscSorted={isCompanyAscSortedRef.current}
          handleSorting={handleSorting}
          columnName={columnName}
        />
      );
    };

    const CreatedHead = () => {
      const handleSorting = () => {
        isCreatedDescSortedRef.current = !isCreatedDescSortedRef.current;
        isCompanyAscSortedRef.current = undefined;
        isAmountAscSortedRef.current = undefined;
        isDueDateDescSortedRef.current = undefined;
        const newSortOptions: SortType = getCreatedSortType(isCreatedDescSortedRef.current);
        setSortOptions(newSortOptions);
        setCurrentPage(DEFAULT_PAGE);
      };

      // handling the sortable field which can only take Ascending value related data
      // adding handing for method to render the column sort order properly
      const sortableFieldAscValue =
        isCreatedDescSortedRef.current !== undefined ? !isCreatedDescSortedRef.current : undefined;

      return (
        <SortableTableHead
          isAscSorted={sortableFieldAscValue}
          handleSorting={handleSorting}
          columnName={InvoiceTableColumns.ISSUED}
        />
      );
    };

    const DueDatedHead = () => {
      const handleSorting = () => {
        isDueDateDescSortedRef.current = !isDueDateDescSortedRef.current;
        isCreatedDescSortedRef.current = undefined;
        isCompanyAscSortedRef.current = undefined;
        isAmountAscSortedRef.current = undefined;
        const newSortOptions: SortType = getDueDateSortType(isDueDateDescSortedRef.current);
        setSortOptions(newSortOptions);
        setCurrentPage(DEFAULT_PAGE);
      };

      // handling the sortable field which can only take Ascending value related data
      // adding handing for method to render the column sort order properly
      const sortableFieldAscValue =
        isDueDateDescSortedRef.current !== undefined ? !isDueDateDescSortedRef.current : undefined;

      return (
        <SortableTableHead
          isAscSorted={sortableFieldAscValue}
          handleSorting={handleSorting}
          columnName={InvoiceTableColumns.DUE}
          tooltipText="Overdues in open invoices will be marked in red"
        />
      );
    };

    const AmountHead = () => {
      const handleSorting = () => {
        isAmountAscSortedRef.current = !isAmountAscSortedRef.current;
        isCreatedDescSortedRef.current = undefined;
        isCompanyAscSortedRef.current = undefined;
        isDueDateDescSortedRef.current = undefined;
        const newSortOptions: SortType = getAmountSortType(isAmountAscSortedRef.current);
        setSortOptions(newSortOptions);
        setCurrentPage(DEFAULT_PAGE);
      };

      return (
        <SortableTableHead
          isAscSorted={isAmountAscSortedRef.current}
          handleSorting={handleSorting}
          columnName={InvoiceTableColumns.AMOUNT}
          className={isDesignIteration1Enabled ? 'text-align-right' : ''}
        />
      );
    };

    let headersMap = invoiceTableCommonHeadersMap();
    headersMap = {
      ...headersMap,
      [InvoiceTableColumns.DUE]: {
        ...headersMap[InvoiceTableColumns.DUE],
        renderComponent: <DueDatedHead />,
      },
      [InvoiceTableColumns.ISSUED]: {
        ...headersMap[InvoiceTableColumns.ISSUED],
        renderComponent: <CreatedHead />,
      },
      [InvoiceTableColumns.AMOUNT]: {
        ...headersMap[InvoiceTableColumns.AMOUNT],
        renderComponent: <AmountHead />,
      },
      [InvoiceTableColumns.COMPANY]: {
        ...headersMap[InvoiceTableColumns.COMPANY],
        renderComponent: <CompanyHead columnName={InvoiceTableColumns.COMPANY} />,
      },
      [InvoiceTableColumns.CUSTOMER]: {
        ...headersMap[InvoiceTableColumns.CUSTOMER],
        renderComponent: <CompanyHead columnName={InvoiceTableColumns.CUSTOMER} />,
      },
      [InvoiceTableColumns.DUNNING_DATE]: {
        name: InvoiceTableColumns.DUNNING_DATE,
        content: InvoiceTableColumns.DUNNING_DATE,
        customClassNames: 'col-dunning-date',
      },
      [InvoiceTableColumns.OUTSTANDING_BALANCE]: {
        name: InvoiceTableColumns.OUTSTANDING_BALANCE,
        content: InvoiceTableColumns.OUTSTANDING_BALANCE,
        customClassNames: 'col-outstanding-balance',
      },
    };

    const getHeaders = memoize(() => {
      if (isDesignIteration1Enabled) {
        const updatedColumns = columns.map((column) => {
          //@ts-ignore
          return headersMap[column];
        });

        return updatedColumns;
      }

      return [
        headersMap[InvoiceTableColumns.INVOICE_NUMBER],
        headersMap[InvoiceTableColumns.STATUS],
        headersMap[InvoiceTableColumns.DUE],
        headersMap[InvoiceTableColumns.ISSUED],
        headersMap[InvoiceTableColumns.AMOUNT],
        headersMap[InvoiceTableColumns.COMPANY],
        headersMap[InvoiceTableColumns.SOLD_TO_ADDRESS],
        headersMap[InvoiceTableColumns.PAYMENT_METHOD],
        headersMap[InvoiceTableColumns.PO_NUMBER],
      ];
    });

    const getTermDetailsHeaders = memoize(() => {
      // TODO: Update accordingly when Credit details page is ready
      return [
        headersMap[InvoiceTableColumns.INVOICE_NUMBER],
        headersMap[InvoiceTableColumns.STATUS],
        headersMap[InvoiceTableColumns.DUE],
        headersMap[InvoiceTableColumns.ISSUED],
        headersMap[InvoiceTableColumns.AMOUNT],
        headersMap[InvoiceTableColumns.DUNNING_DATE],
        headersMap[InvoiceTableColumns.OUTSTANDING_BALANCE],
      ];
    });

    const [columnsList, setColumnList] = useState<Array<ColumnList>>(
      shouldShowDeferredInvoicesColumns ? getTermDetailsHeaders() : getHeaders()
    );

    const getSoldToAddress = (shipTo: InvoiceBillAndShipTo | undefined | null) => {
      return (
        <SoldToAddress
          shipTo={{
            id: shipTo?.id,
            addressLine1: shipTo?.address?.address1,
            addressLine2: shipTo?.address?.address2,
            city: shipTo?.address?.city,
            country: shipTo?.address?.country,
            postcode: shipTo?.address?.postalCode,
            state: shipTo?.address?.state,
          }}
          txaId={transactionAccountId}
          shouldDisableLink={isDesignIteration1Enabled}
        />
      );
    };

    const getCompanyName = (shipTo: InvoiceBillAndShipTo | undefined | null) => {
      if (!shipTo?.fullName) {
        return <NoData />;
      } else {
        return (
          <SoldToAddressDetailsLink
            txaId={transactionAccountId}
            companyName={shipTo.fullName}
            shouldDisableLink={!shipTo?.id}
          >
            <span className={isDesignIteration1Enabled ? '' : 'line-clamp'}>
              <FormattedAddress organizationName={shipTo?.fullName} />
            </span>
          </SoldToAddressDetailsLink>
        );
      }
    };

    const onPageChange = useCallback(
      (newPage: number) => {
        setCurrentPage(newPage);
        refetch({
          searchOptions: {
            offset: (newPage - 1) * rowsPerPage,
            first: rowsPerPage,
            sort: sortOptions,
          },
          filterOptions: filterOptions,
        });

        refetchRefund({
          searchOptions: {
            offset: (newPage - 1) * rowsPerPage,
            first: rowsPerPage,
            sort: sortOptions,
          },
          filterOptions: filterOptions,
        });
      },
      [[rowsPerPage, sortOptions, filterOptions]]
    );

    const handleRowChange = useCallback(
      (rowsCount: number) => {
        setRowsPerPage(rowsCount);
        setCurrentPage(1);
        refetch({
          searchOptions: {
            first: rowsCount,
            offset: 0,
            sort: sortOptions,
          },
          filterOptions: filterOptions,
        });

        refetchRefund({
          searchOptions: {
            first: rowsCount,
            offset: 0,
            sort: sortOptions,
          },
          filterOptions: filterOptions,
        });
      },
      [sortOptions, filterOptions, rowsPerPage, currentPage]
    );

    const getPaymentMethodContent = ({
      paymentMethod,
      creditCard,
      invoicePaymentDueDays,
      hasPayByStatement,
      paypalAccountEmail,
    }: {
      paymentMethod: InvoicePaymentMethod | null | undefined;
      creditCard: CreditCardDetails | undefined | null;
      invoicePaymentDueDays: number | null | undefined;
      hasPayByStatement?: boolean;
      paypalAccountEmail: string | null | undefined;
    }) => {
      if (isPaypalEnabled && isPaypalPaymentMethod(paymentMethod)) {
        return <PaypalPaymentMethodInTable emailID={paypalAccountEmail ?? ''} />;
      }
      const paymentMethodText = getPaymentMethodText(
        paymentMethod,
        creditCard,
        invoicePaymentDueDays,
        hasPayByStatement
      );
      if (paymentMethodText) {
        return paymentMethodText;
      }
      return <NoData />;
    };

    const getColumnData = (visibleColumns: ColumnList[], invoice: Invoice) => {
      const {
        id,
        invoiceNumber,
        status,
        dueDate,
        grandTotal,
        paymentCurrency,
        pricingCurrency,
        createdDate,
        paymentMethod,
        shipTo,
        refunds,
        creditCard,
        prePaymentCreditNotesAmount,
        dunningStatus,
        paymentMethodId,
        billingGroupId,
        paypalAccountEmail,
      } = invoice;

      const isDeferredAndHasStatement = invoice?.paymentMethod === InvoicePaymentMethod?.DEFERRED && hasPayByStatement;
      const isDeferredPaymentMethodSelected = deferredPaymentMethod?.some(
        (paymentMethod) => paymentMethod?.id === paymentMethodId
      );

      const invoicePaymentDueDays =
        deferredPaymentMethod && deferredPaymentMethod[0]?.id === paymentMethodId
          ? deferredPaymentMethod[0]?.terms?.invoiceDueDays
          : null;

      const rowData = visibleColumns.map((column) => {
        switch (column.name) {
          case InvoiceTableColumns.INVOICE_NUMBER:
            return (
              <td>
                <InvoiceCell
                  invoiceId={id}
                  status={status}
                  invoiceNumber={invoiceNumber || ''}
                  paymentError={isDeferredPaymentMethodSelected ? false : !!invoice?.dunningStatus?.length}
                  transactionAccountId={transactionAccountId}
                  grandTotal={grandTotal}
                  refunds={refunds as Refunds[]}
                  invoiceLink={getRouteUrl(routePaths.invoiceDetails, {
                    txaId: transactionAccountId || '',
                    invoiceId: encodeURIComponent(invoiceNumber || ''),
                  })}
                />
              </td>
            );
          case InvoiceTableColumns.STATUS:
            return (
              <td>
                {shouldShowDeferredInvoicesColumns ? getTermDetailsInvoiceStatus(status) : getInvoiceStatus(status)}
              </td>
            );
          case InvoiceTableColumns.CUSTOMER:
          case InvoiceTableColumns.COMPANY:
            return (
              <td data-testid="organisation-name" className="customer-name">
                {getCompanyName(shipTo)}
              </td>
            );
          case InvoiceTableColumns.SOLD_TO_ADDRESS:
            return <td>{getSoldToAddress(shipTo)}</td>;
          case InvoiceTableColumns.PAYMENT_METHOD:
            return (
              <td data-testid="invoice-payment-method">
                {getPaymentMethodContent({
                  paymentMethod,
                  creditCard,
                  invoicePaymentDueDays,
                  hasPayByStatement,
                  paypalAccountEmail,
                })}
              </td>
            );
          case InvoiceTableColumns.DUE:
            return (
              <td>
                {dueDate ? (
                  <div
                    className={classNames({
                      'overdue-date':
                        !isDeferredAndHasStatement &&
                        InvoiceUtil.isDueDateOverdue(status ?? '', !!invoice?.dunningStatus?.length, dueDate),
                    })}
                  >
                    {isDeferredAndHasStatement ? (
                      <NoData testId="empty-invoice-due-date" />
                    ) : (
                      <FormattedDate date={dueDate} format={NEW_DATE_FORMAT} />
                    )}
                  </div>
                ) : (
                  <NoData />
                )}
              </td>
            );
          case InvoiceTableColumns.ISSUED:
            return <td>{createdDate ? <FormattedDate date={createdDate} format={NEW_DATE_FORMAT} /> : <NoData />}</td>;
          case InvoiceTableColumns.PO_NUMBER:
            return (
              <td data-testid="po-number">
                {!isNil(invoice?.purchaseOrderNumber) ? invoice.purchaseOrderNumber : <NoData />}
              </td>
            );
          case InvoiceTableColumns.AMOUNT:
            return (
              <td className={classNames('invoice-amount-cell', { 'text-align-right': isDesignIteration1Enabled })}>
                {shouldShowDeferredInvoicesColumns ? (
                  getTermDetailsInvoiceAmount(grandTotal as number, pricingCurrency ?? paymentCurrency)
                ) : !getIsFreeInvoice(paymentMethod) && grandTotal ? (
                  <FormatCurrency
                    value={grandTotal}
                    currency={pricingCurrency ?? paymentCurrency}
                    showCurrencyCode
                    showCommas
                  />
                ) : (
                  'Free'
                )}
              </td>
            );
          case InvoiceTableColumns.OUTSTANDING_BALANCE:
            return (
              <td>
                {status === InvoiceStatus.PAYMENT_DEFERRED ? (
                  <FormatCurrency value={0} currency={pricingCurrency ?? paymentCurrency} showCurrencyCode showCommas />
                ) : grandTotal ? (
                  <FormatCurrency
                    value={
                      grandTotal && prePaymentCreditNotesAmount ? grandTotal - prePaymentCreditNotesAmount : grandTotal
                    }
                    currency={pricingCurrency ?? paymentCurrency}
                    showCurrencyCode
                    showCommas
                  />
                ) : (
                  <NoData />
                )}
              </td>
            );
          case InvoiceTableColumns.DUNNING_DATE:
            return (
              <td>
                {dunningStatus ? (
                  <FormattedDate date={get(dunningStatus, '[0].nextPaymentAttempDate')} format={NEW_DATE_FORMAT} />
                ) : (
                  <NoData />
                )}
              </td>
            );
          case InvoiceTableColumns.BILLING_PROFILE:
            return (
              <td>
                <BillingProfileNameFromId
                  id={billingGroupId}
                  txaId={transactionAccountId || ''}
                  showBillingProfileNumber={showBillingProfileNumber}
                />
              </td>
            );
        }
      });
      return rowData;
    };

    const refundsRoutePPC = getRouteUrl(routePaths.refunds, { txaId: transactionAccountId || '' });

    const currencyFilterOption = useMemo(() => {
      return isMultiCurrencyChangesEnabled && currency !== ''
        ? [
            {
              key: 'pricingCurrency',
              value: currency,
            },
          ]
        : [];
    }, [currency, isMultiCurrencyChangesEnabled]);

    const onSearch = useCallback(
      async (formData: FormData) => {
        const currentSearchOptions: any = [];
        const searchInput = formData?.searchTerm;
        setIsSearched(!!searchInput);
        if (searchInput) {
          // Use text variants for better search matching
          const searchTextVariantsInvoice = getSearchTextVariants('invoiceNumber', searchInput);
          const searchTextVariantsPurchaseOrder = getSearchTextVariants('purchaseOrderNumber', searchInput);
          /* `containsNameMultiMatch` matches on any of the 'ship_to.display_name', 'bill_to.display_name',
          or 'credit_card_holder_name.exact' field. Here, we want to handle partial matching for 'Company' column
          values which shows `shipTo` name.
          Also, `containsNameMultiMatch` handles the text variants internally.
          */
          const searchTextVariantsNameMultiMatch = [{ containsNameMultiMatch: searchInput }];
          const organizationNameSearch = [{ organization: searchInput }];
          currentSearchOptions.push(
            ...searchTextVariantsInvoice,
            ...searchTextVariantsPurchaseOrder,
            ...searchTextVariantsNameMultiMatch,
            ...(isOrgSearchEnabled ? organizationNameSearch : [])
          );
          trackSearchEvent();
        }
        const updatedFilterOptions =
          statusFilterOptions.length > 0
            ? {
                or: currentSearchOptions,
                and: [
                  { or: statusFilterOptions },
                  { or: filters },
                  ...createdDateRange,
                  ...dueDateRange,
                  ...currencyFilterOption,
                ],
              }
            : {
                or: currentSearchOptions,
                and: [{ or: filters }, ...createdDateRange, ...dueDateRange, ...currencyFilterOption],
              };
        setFilterOptions(updatedFilterOptions);
        setSearchInputOptions(currentSearchOptions);
        setCurrentPage(1);
      },
      [statusFilterOptions, filters, createdDateRange, dueDateRange, currencyFilterOption]
    );

    const handleStatusFilter = useCallback(
      (selectedFilters: { key: string; value: string }[] | Record<string, string>[]) => {
        setIsStatusFilterApplied(selectedFilters?.length > 0);
        /**
         For invoices which are paid using payment on terms method, if the invoice status is "PAYMENT_DEFERRED" we consider it as "PAID"
         bcoz it will later be paid using bank-transfer, cheques, cash etc. Hence if the selected filter is PAID here, we would show the invoices
         which has status as "PAYMENT_DEFERRED".
         */

        if (selectedFilters.find((item) => item.status === InvoiceStatus.PAID)) {
          selectedFilters = [...selectedFilters, { status: InvoiceStatus.PAYMENT_DEFERRED }];
        }

        {
          selectedFilters.length > 0
            ? setStatusFilterOptions(selectedFilters)
            : setStatusFilterOptions(appropriateTabFilters);
        }

        const updatedFilterOptions =
          selectedFilters.length > 0
            ? {
                or: searchInputOptions,
                and: [
                  { or: selectedFilters },
                  { or: filters },
                  ...createdDateRange,
                  ...dueDateRange,
                  ...currencyFilterOption,
                ],
                // don't show on-account invoices on the UI if any of the due-date filter is selected
                ...((hasPayByStatement || hasPayOnAccountFromUrl) && dueDateRange.length > 0
                  ? { not: [{ paymentMethod: InvoicePaymentMethod.DEFERRED }] }
                  : []),
              }
            : {
                or: searchInputOptions,
                and: [
                  { or: appropriateTabFilters },
                  { or: filters },
                  ...createdDateRange,
                  ...dueDateRange,
                  ...currencyFilterOption,
                ],
                // don't show on-account invoices on the UI if any of the due-date filter is selected
                ...((hasPayByStatement || hasPayOnAccountFromUrl) && dueDateRange.length > 0
                  ? { not: [{ paymentMethod: InvoicePaymentMethod.DEFERRED }] }
                  : []),
              };
        setFilterOptions(updatedFilterOptions);
        setCurrentPage(1);
      },
      [searchInputOptions, filters, createdDateRange, dueDateRange, INVOICE_TABLE_TAB_FILTERS]
    );

    const handleCurrencyFilter = (selectedCurrency: string) => {
      const currencyFilterOption = selectedCurrency
        ? [
            {
              key: 'pricingCurrency',
              value: selectedCurrency,
            },
          ]
        : [];

      const updatedFilterOptions =
        statusFilterOptions.length > 0
          ? {
              or: searchInputOptions,
              and: [
                { or: statusFilterOptions },
                { or: filters },
                ...createdDateRange,
                ...dueDateRange,
                ...currencyFilterOption,
              ],
            }
          : {
              or: searchInputOptions,
              and: [{ or: filters }, ...createdDateRange, ...dueDateRange, ...currencyFilterOption],
            };
      setFilterOptions(updatedFilterOptions);
      setCurrentPage(1);
    };

    const handleDateRangeFilter = (key: string, value: DateRangeType) => {
      const fromDate = {
        key: key + 'IsGte',
        value: value.from,
      };
      const toDate = {
        key: key + 'IsLte',
        value: value.to,
      };

      if (key === dateRangeKeys.createdDate) {
        setCreatedDateRange([fromDate, toDate]);
      } else if (key === dateRangeKeys.dueDate) {
        setDueDateRange([fromDate, toDate]);
      }

      const updatedFilterOptions =
        statusFilterOptions.length > 0
          ? {
              or: searchInputOptions,
              and: [
                { or: statusFilterOptions },
                { or: filters },
                // The current called `from` and `to` values should override the existing state values. So, if
                // 'createdDate' is applied, it should override the `createdDateRange` and not `dueDateRange`.
                // Not using state variables `createdDateRange` and `dueDateRange` below, as the new state is not set yet.
                ...(key === dateRangeKeys.createdDate ? dueDateRange : createdDateRange),
                fromDate,
                toDate,
                ...currencyFilterOption,
              ],
              // don't show on-account invoices on the UI if any of the due-date filter is selected
              ...((hasPayByStatement || hasPayOnAccountFromUrl) &&
              (key === dateRangeKeys.dueDate || dueDateRange.length > 0)
                ? { not: [{ paymentMethod: InvoicePaymentMethod.DEFERRED }] }
                : []),
            }
          : {
              or: searchInputOptions,
              and: [
                { or: filters },
                ...(key === dateRangeKeys.createdDate ? dueDateRange : createdDateRange),
                fromDate,
                toDate,
                ...currencyFilterOption,
              ],
              // don't show on-account invoices on the UI if any of the due-date filter is selected
              ...((hasPayByStatement || hasPayOnAccountFromUrl) &&
              (key === dateRangeKeys.dueDate || dueDateRange.length > 0)
                ? { not: [{ paymentMethod: InvoicePaymentMethod.DEFERRED }] }
                : []),
            };
      setFilterOptions(updatedFilterOptions);
      setCurrentPage(1);
    };

    const getCreatedDateRange = (value: any, isCustomFilter?: boolean) => {
      const rangeValue = getAppropriateDateRangeValue(value, isCustomFilter);

      handleDateRangeFilter(dateRangeKeys.createdDate, rangeValue);
    };

    const getDueDateRange = (value: any, isCustomFilter?: boolean) => {
      const rangeValue = isCustomFilter
        ? getAppropriateDateRangeValue(value, isCustomFilter)
        : getAppropriateDueDateRangeValue(value, isCustomFilter);

      handleDateRangeFilter(dateRangeKeys.dueDate, rangeValue);
    };

    const clearAllFilters = () => {
      setCurrentPage(1);
      setStatusFilterResetCount(statusFilterResetCount + 1);
      setStatusFilterFields(initialStatusFilterFields); // Reset the pre-selected Status fields
      setStatusFilterOptions(appropriateTabFilters);
      setSelectedCreatedDateRangeValue('');
      setSelectedDueDateRangeValue('');
      setCreatedDateRange([]);
      setDueDateRange([]);
      setIsStatusFilterApplied(false);
      setCurrency('');
      const updatedFilterOptions = {
        or: searchInputOptions, // Keep the search results
        and: [{ or: appropriateTabFilters }, { or: filters }],
      };
      setFilterOptions(updatedFilterOptions);
    };

    const clearAllTabFilters = () => {
      setCurrentPage(1);
      setSelectedCreatedDateRangeValue('');
      setSelectedDueDateRangeValue('');
      setCreatedDateRange([]);
      setDueDateRange([]);
      setCurrency('');
      setIsStatusFilterApplied(false);
      const updatedFilterOptions = {
        or: searchInputOptions,
        and: [{ or: filters }],
      };
      setFilterOptions(updatedFilterOptions);
    };

    const isAnyFilterApplied =
      dueDateRange.length > 0 || createdDateRange.length > 0 || isStatusFilterApplied || isCurrencyFilterApplied;
    const isAnyTabFilterApplied = dueDateRange.length > 0 || createdDateRange.length > 0 || isCurrencyFilterApplied;

    const SEARCH_BOX_HELPER_MESSAGE = `Search by IN-#,${
      isOrgSearchEnabled ? ' organization,' : ''
    } company or PO number`;
    const SEARCH_PLACEHOLDER_TEXT = isDesignIteration1Enabled ? 'Search invoices' : 'Search';

    const renderCustomSearch = (customSearchKey?: string) => {
      return (
        <CustomSearch
          placeholder={SEARCH_PLACEHOLDER_TEXT}
          onSearch={onSearch}
          isLoading={loading}
          isClearable={true}
          helperMessage={SEARCH_BOX_HELPER_MESSAGE}
          // Key is needed to clear search input on interface (button tab) switch in invoices page
          {...(isDesignIteration1Enabled && { key: customSearchKey })}
        />
      );
    };

    const renderIssueDateFilter = () => {
      return (
        <DateFilterWithBadge
          dateValue={getCreatedDateRange}
          selected={selectedCreatedDateRangeValue}
          onSelected={(selected: string) => {
            setSelectedCreatedDateRangeValue(selected);
          }}
          placeholder="Issue date"
        />
      );
    };

    const renderDueDateFilter = () => {
      return (
        <DateFilterWithBadge
          dateValue={getDueDateRange}
          selected={selectedDueDateRangeValue}
          onSelected={(selected: string) => {
            setSelectedDueDateRangeValue(selected);
          }}
          placeholder="Due date"
          dateRangeOptions={dueDateRangeOption}
        />
      );
    };

    const renderCurrencyFilter = () => {
      return (
        <div>
          <LimitCurrencyDropdown
            currency={currency}
            setCurrency={(value) => {
              const selectedCurrency = value ? (value as string) : '';
              setCurrency(selectedCurrency);
              handleCurrencyFilter(selectedCurrency);
            }}
            showLabel={false}
            isLightTheme
            isFilter
            showAllCurrency
            shouldPrependCurrencyText={isDesignIteration1Enabled}
            isDesignIteration1Enabled={isDesignIteration1Enabled}
          />
        </div>
      );
    };

    const renderFilters = () => {
      // To improve: Move filters into a separate component
      return (
        <div className="invoice-table-filter">
          {renderCustomSearch('invoice-table-search')}
          <CustomFilterCheckboxWrapper
            filterFields={statusFilterFields}
            handleFilterUpdate={handleStatusFilter}
            shouldHideClearFilterButton={true}
            resetFromParent={statusFilterResetCount}
          />
          {renderIssueDateFilter()}
          {renderDueDateFilter()}
          {isMultiCurrencyChangesEnabled && renderCurrencyFilter()}
          {isAnyFilterApplied && (
            <Button appearance="link" testId="invoice-filter-reset-link" onClick={clearAllFilters}>
              Clear all filters
            </Button>
          )}
        </div>
      );
    };

    const renderFiltersTabs = () => {
      return (
        <div className="invoice-table-filter">
          {renderCustomSearch('refunds-table-search')}
          {renderIssueDateFilter()}
          {renderDueDateFilter()}
          {isMultiCurrencyChangesEnabled && renderCurrencyFilter()}
          {isAnyTabFilterApplied && (
            <Button appearance="link" testId="invoice-filter-tab-reset-link" onClick={clearAllTabFilters}>
              Clear all filters
            </Button>
          )}
        </div>
      );
    };

    const emptyStatePropForSearch = isDesignIteration1Enabled
      ? emptyStatePropForWrongSearch
      : emptyStateWithDescriptionProp;

    const emptyStateProp = isDesignIteration1Enabled
      ? selectedInterfaceKey === refundedTabNameKey
        ? emptyStatePropForRefundsTable
        : emptyStatePropForInvoiceTable
      : emptyStateWithDescriptionProp;

    const renderTableBody = (nodes: any[]) => {
      return (
        <>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/*@ts-ignore*/}
          {(nodes ?? []).map((invoice: Invoice, index: number) => {
            const { refunds, id } = invoice;
            const mainRow = getColumnData(getSelectedColumns(columnsList), invoice).map((node) => node);
            const isRefundsPresent = !isEmpty(refunds);
            const renderRefunds = () => {
              return (
                <>
                  {isRefundsPresent &&
                    //@ts-ignore
                    refunds?.map((ccpCreditNote: Refunds, indexRefund: number) => {
                      return (
                        <RefundChildRow
                          key={`invoice-refund-row-${ccpCreditNote.creditNoteNumber || ccpCreditNote.id}`}
                          //@ts-ignore
                          refund={ccpCreditNote}
                          isSelected={isSidebarOpen}
                          index={index}
                          refundDetailsLink={`${refundsRoutePPC}${encodeURIComponent(
                            ccpCreditNote.creditNoteNumber || ccpCreditNote.id
                          )}`}
                          shouldDisableSidebar={true}
                          visibleColumns={getSelectedColumns(columnsList)}
                          transactionAccountId={transactionAccountId}
                          isCreditNoteIconFunctional={isCreditNoteIconFunctional}
                          isDesignIteration1Enabled={isDesignIteration1Enabled}
                        />
                      );
                    })}
                </>
              );
            };

            if (isDesignIteration1Enabled) {
              if (!isRefundsPresent) {
                return (
                  <tr data-testid={`invoice-table-row-${index}`} key={id}>
                    {mainRow}
                  </tr>
                );
              }

              return (
                <ExpandableRow
                  testId={`invoice-refund-expandable-row${index}`}
                  key={`expandable-row-key-${id}`}
                  isDefaultExpanded={true}
                  rowsAsCells={[mainRow]}
                  subRows={renderRefunds()}
                />
              );
            }

            return (
              <>
                <tr data-testid={`invoice-table-row-${index}`}>{mainRow}</tr>
                {renderRefunds()}
              </>
            );
          })}
        </>
      );
    };

    const getTabProps = (refundNodes: any[]) => ({
      children: (
        <>
          <ColGroup columns={getSelectedColumns(columnsList)} />
          {<TableHead columns={getSelectedColumns(columnsList)} />}
          <TableBody>{renderTableBody(refundNodes)}</TableBody>
        </>
      ),
      noResults: totalRefundInvoices === 0,
      isLoading: refundLoading,
      error: refundError?.toString(),
      selectableResults: true,
      isPaginationEnabled: totalRefundInvoices > DEFAULT_ROWS_PER_PAGE,
      totalCount: totalRefundInvoices,
      rowsPerPage: rowsPerPage,
      currentPage: currentPage,
      onPageChange: onPageChange,
      onRowChange: handleRowChange,
      testId: 'transaction-account-refunded-invoice-tab',
      emptyStateProps: isSearched ? emptyStatePropForSearch : emptyStateProp,
      errorStateProps: errorStateProp,
    });

    const renderInnerHeadingText = () => {
      const hasPendingInvoices = allInvoicesData?.data?.transactionAccount?.pendingInvoices;

      if (!shouldShowDeferredInvoicesColumns || !hasPendingInvoices || shouldFetchOnlyOverdueInvoices) return null;
      return (
        <div className="ppc-inflight-invoice-banner">
          <InfoPanel
            type={isDesignIteration1Enabled ? 'information' : 'warning'}
            message="There are inflight invoices associated to this account."
          />
        </div>
      );
    };

    const renderActions = () => {
      return [
        () =>
          showDownloadCSVButton ? (
            <LoadingButton
              onClick={() => {
                //@ts-ignore
                downloadInvoicesListCSV();
              }}
              testId="export-invoices-as-csv"
              isLoading={invoicesCSVListLoading}
              iconBefore={<ExportIcon label="Export icon" size="small" />}
            >
              Export as CSV
            </LoadingButton>
          ) : (
            <></>
          ),
      ];
    };

    const getProps = (nodes: any[]) => ({
      children: (
        <>
          <ColGroup columns={getSelectedColumns(columnsList)} />
          {<TableHead columns={getSelectedColumns(columnsList)} />}
          <TableBody>{renderTableBody(nodes)}</TableBody>
        </>
      ),
      noResults: hasNoResults,
      isLoading: loading,
      error: error?.toString(),
      selectableResults: true,
      isPaginationEnabled: totalCount > DEFAULT_ROWS_PER_PAGE,
      totalCount: totalCount,
      rowsPerPage: rowsPerPage,
      currentPage: currentPage,
      onPageChange: onPageChange,
      onRowChange: handleRowChange,
      testId: 'transaction-account-invoice-tab',
      emptyStateProps: isSearched ? emptyStatePropForSearch : emptyStateProp,
      errorStateProps: errorStateProp,
      innerHeading: renderInnerHeadingText(),
    });

    const tabContentClassName = classNames('transaction-account-invoice-tab', {
      'invoice-table-content-v2': isDesignIteration1Enabled,
    });

    const renderInvoiceContent = () => {
      const invoicesAttribute = shouldFetchOnlyOverdueInvoices ? 'overdueInvoices' : 'invoices';
      //@ts-ignore
      const nodes = data?.transactionAccount?.[invoicesAttribute]?.nodes;
      return (
        <div data-testid="transaction-account-invoice-tab" className={tabContentClassName}>
          <CustomDynamicResultTable
            {...getProps(nodes)}
            customiseColumnView={
              shouldHideCustomiseView ? (
                <></>
              ) : (
                <CustomizeView
                  columns={columnsList}
                  columnCount={9}
                  updatedColumnView={(record: Array<HeadCell>) => setColumnList([...record] as Array<ColumnList>)}
                />
              )
            }
            className={shouldShowDeferredInvoicesColumns ? 'ppc-deferred-invoices-table' : 'invoice-transaction-table'}
            shouldForceEnableFilters={!shouldFetchOnlyOverdueInvoices}
            filters={renderFilters()}
            isSearched={isSearched}
            isAnyFilterApplied={isAnyFilterApplied}
            actions={renderActions()}
          ></CustomDynamicResultTable>
        </div>
      );
    };

    const renderRefundedContent = () => {
      const refundNodes = refundsData?.transactionAccount?.refundedInvoices?.nodes ?? [];

      return (
        <div data-testid="transaction-account-refunded-invoice-tab" className={tabContentClassName}>
          <CustomDynamicResultTable
            {...getTabProps(refundNodes)}
            customiseColumnView={
              shouldHideCustomiseView ? (
                <></>
              ) : (
                <CustomizeView
                  columns={columnsList}
                  columnCount={9}
                  updatedColumnView={(record: Array<HeadCell>) => setColumnList([...record] as Array<ColumnList>)}
                />
              )
            }
            className="invoice-transaction-table"
            shouldForceEnableFilters={!shouldFetchOnlyOverdueInvoices}
            filters={renderFiltersTabs()}
            isSearched={isSearched}
            isAnyFilterApplied={isAnyFilterApplied}
            actions={renderActions()}
          ></CustomDynamicResultTable>
        </div>
      );
    };

    const getInnerTabValue = useCallback((searchUrl?: string) => {
      const params = new URLSearchParams(searchUrl);
      return defaultTo(params?.get('inner_tab'), '');
    }, []);

    const [selectedTabKey, setSelectedTabKey] = useState<string>(
      selectedInterfaceKey ?? getInnerTabValue(location?.search)
    );

    const handleSortOnTabSelect = (): SortType => {
      isCreatedDescSortedRef.current = DefaultIsCreatedDescSorted;
      isCompanyAscSortedRef.current = undefined;
      isAmountAscSortedRef.current = undefined;
      isDueDateDescSortedRef.current = undefined;
      return getCreatedSortType(DefaultIsCreatedDescSorted);
    };

    const handleTabSelect = (tabLabel: string) => {
      setSelectedTabKey(kebabCase(tabLabel));
      const newSortType = handleSortOnTabSelect();
      clearAllTabFilters();
      setSearchInputOptions([]);
      setSortOptions(newSortType);
      setRowsPerPage(DEFAULT_ROWS_PER_PAGE);
      if (shouldShowDeferredInvoicesColumns) {
        tabLabel === openAndPaidTabName ? setStatusFilterOptions(appropriateTabFilters) : setStatusFilterOptions([]);
        setFilterOptions({
          and: tabLabel === openAndPaidTabName ? [{ or: appropriateTabFilters }, { or: filters }] : [{ or: filters }],
        });
        setStatusFilterFields(initialStatusFilterFields);
        setCurrentPage(1);
      } else if (tabLabel === openAndPaidTabName) {
        setStatusFilterOptions(INVOICE_TABLE_TAB_FILTERS);
        setFilterOptions({
          and: [{ or: INVOICE_TABLE_TAB_FILTERS }],
          or: [...filters],
        });
        setStatusFilterFields(initialStatusFilterFields); // Reset the pre-selected Status fields
        setCurrentPage(1);
      } else {
        setStatusFilterOptions([]);
        setFilterOptions({
          and: [],
          or: [...filters],
        });
      }
    };

    return (
      <div
        className={classNames({
          'tab-container-invoice': !isDesignIteration1Enabled,
          'tab-container-invoice-with-label': label,
        })}
      >
        {label && !isDesignIteration1Enabled && <TableHeading className="pt-32 ph-28">{label}</TableHeading>}
        {isDesignIteration1Enabled ? (
          <div>{isRefundedInterface ? renderRefundedContent() : renderInvoiceContent()}</div>
        ) : (
          <Tabs testId="invoice-status-tabs" onTabSelect={handleTabSelect} selectedTabKey={selectedTabKey}>
            <TabPanel label={openAndPaidTabName}>{renderInvoiceContent()}</TabPanel>
            <TabPanel label={refundedTabName}>{renderRefundedContent()}</TabPanel>
          </Tabs>
        )}
      </div>
    );
  }
);

export default InvoiceTable;
